import { QueryKey } from '@tanstack/react-query';

import { DriveFolderRef } from '../model/driveFolders';
import { PlanLine } from '../model/driveSummary';
import { DriveCopyRequest, DrivePreviewRequest, DriveShareRequest } from '../model/types';
import useArtworkDriveService from './artworkDrive.service';
import { artworkDriveApi, artworkDriveKeys } from './artworkDriveApi';
import { useDriveErrorToast } from './useDriveErrorToast';

const NO_KEYS: QueryKey[] = [];

/**
 * Drive writes on one order. [alsoInvalidate] names what the caller's screen renders from Drive's effects — the
 * order page passes its own order keys, since a refresh or a copy changes the order's artwork rows.
 */
export const useOrderDriveMutations = (orderId: string, alsoInvalidate: QueryKey[] = NO_KEYS) => {
  const service = useArtworkDriveService();
  const onError = useDriveErrorToast();

  const share = artworkDriveApi.useFeatureMutation({
    mutationFn: ({ folder, request }: { folder: DriveFolderRef; request: DriveShareRequest }) =>
      service.shareFolder(orderId, folder, request),
    invalidate: (_data, { folder }) => [artworkDriveKeys.folderAccess(orderId, folder)],
    successMessage: 'artwork_drive.access.shared',
    onError,
  });

  const unshare = artworkDriveApi.useFeatureMutation({
    mutationFn: ({ folder, permissionId }: { folder: DriveFolderRef; permissionId: string }) =>
      service.unshareFolder(orderId, folder, permissionId),
    invalidate: (_data, { folder }) => [artworkDriveKeys.folderAccess(orderId, folder)],
    successMessage: 'artwork_drive.access.removed',
    onError,
  });

  const uploadIntent = artworkDriveApi.useFeatureMutation({
    mutationFn: (folder: DriveFolderRef) => service.uploadIntent(orderId, folder),
    invalidate: false,
    onError,
  });

  const refresh = artworkDriveApi.useFeatureMutation({
    mutationFn: (folder: DriveFolderRef) => service.refreshFolder(orderId, folder),
    invalidate: [artworkDriveKeys.order(orderId), ...alsoInvalidate],
    onError,
  });

  const copy = artworkDriveApi.useFeatureMutation({
    // The copy is a new Drive file; refreshing its folder shows it now rather than at the next ingest. A failed refresh
    // leaves that to the ingest - the copy itself is done.
    mutationFn: async (request: DriveCopyRequest) => {
      const copied = await service.copyToDecoration(orderId, request);
      await service
        .refreshFolder(orderId, { kind: 'decoration', operationId: request.targetOperationId })
        .catch(() => undefined);
      return copied;
    },
    invalidate: [artworkDriveKeys.order(orderId), ...alsoInvalidate],
    successMessage: 'artwork_drive.copy.copied',
    onError,
  });

  const generatePreviews = artworkDriveApi.useFeatureMutation({
    mutationFn: ({ operationId, request }: { operationId: string; request: DrivePreviewRequest }) =>
      service.generatePreviews(orderId, operationId, request),
    invalidate: false,
    successMessage: 'artwork_drive.previews.queued',
    onError,
  });

  // Only queued here: the files reach the folder later, and the requester hears of each link by notification. The
  // order rides in the variables: a copy offered after a links save runs once the host has moved off that order.
  const importWeTransfer = artworkDriveApi.useFeatureMutation({
    mutationFn: ({
      orderId: importOrderId,
      folder,
      link,
    }: {
      orderId: string;
      folder: DriveFolderRef;
      link: string;
    }) => service.importWeTransfer(importOrderId, folder, link),
    invalidate: false,
    onError,
  });

  return { share, unshare, uploadIntent, refresh, copy, generatePreviews, importWeTransfer };
};

export const usePlanDriveMutations = (planId: string) => {
  const service = useArtworkDriveService();
  const onError = useDriveErrorToast();

  const share = artworkDriveApi.useFeatureMutation({
    mutationFn: (request: DriveShareRequest) => service.sharePlan(planId, request),
    invalidate: [artworkDriveKeys.planAccess(planId)],
    successMessage: 'artwork_drive.access.shared',
    onError,
  });

  const unshare = artworkDriveApi.useFeatureMutation({
    mutationFn: (permissionId: string) => service.unsharePlan(planId, permissionId),
    invalidate: [artworkDriveKeys.planAccess(planId)],
    successMessage: 'artwork_drive.access.removed',
    onError,
  });

  const importWeTransfer = artworkDriveApi.useFeatureMutation({
    mutationFn: ({ link, line }: { link: string; line?: PlanLine }) =>
      service.importPlanWeTransfer(planId, link, line),
    invalidate: false,
    onError,
  });

  return { share, unshare, importWeTransfer };
};
